import { Filter } from "@/shared/lib/filter/filter";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { Address } from "@/services/backend/addresses/types/address";
import { Representative } from "@/services/backend/representatives/types/representative";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";

import { downloadResponseHandler } from "@/services/backend/_lib/pdf-helpers";
import {
  objectURLResponseHandler,
  revokeObjectURLOptions,
} from "@/shared/lib/utilities/object-url";
import { Lst } from "@/shared/lib/lst/lst";
import { backendApi } from "@/services/backend/api";
import { Offer } from "@/services/backend/rlt/offers/types/offer";
import { updateCache } from "@/services/backend/_lib/update-cache";
import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import { DocumentState } from "@/services/backend/documents/types/state";

const offerApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    rltOfferCreate: build.mutation<Offer, CreateRequest>({
      query: (request) => ({
        url: `/rlt/offer`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltOffer", id: request.id },
        "RltOffer",
        "RltPricingByArea",
      ],
    }),
    rltOfferCreateFromOld: build.mutation<Offer, CreateFromOldRequest>({
      query: (request) => ({
        url: `/rlt/offer/create-from-old`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [
        { type: "RltOffer", id: "LIST" },
        "RltOffer",
        "RltPricingByArea",
      ],
    }),
    rltOfferCreateFromCalculation: build.mutation<
      Offer,
      CreateFromCalculationRequest
    >({
      query: (request) => ({
        url: `/rlt/offer/create-from-calculation`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [
        { type: "RltOffer", id: "LIST" },
        "RltOffer",
        "RltPricingByArea",
      ],
    }),
    rltOfferShow: build.query<Offer, ShowRequest>({
      query: (request) => ({
        url: `/rlt/offer`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "RltOffer", id: result.id }] : [],
    }),
    rltOfferList: build.query<Lst<Offer>, Partial<ListRequest>>({
      query: (request) => ({
        url: `/rlt/offer/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "RltOffer"),
    }),
    rltOfferUpdateDate: build.mutation<Offer, UpdateDateRequest>({
      query: (request) => ({
        url: `/rlt/offer/offer-date`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltOffer", id: request.id },
        "RltOffer",
      ],
    }),
    rltOfferPatch: build.mutation<Offer, PatchRequest>({
      query: (request) => ({
        url: `/rlt/offer`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltOffer", id: request.id },
        { type: "RltOffer", id: "LIST" },
      ],
    }),
    rltOfferResetReferenceNumber: build.mutation<
      Offer,
      ResetReferenceNumberRequest
    >({
      query: (request) => ({
        url: `/rlt/offer/reset-reference-number`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltOffer", id: request.id },
        { type: "RltOffer", id: "LIST" },
      ],
    }),
    rltOfferUpdateTreatmentPriceUseCalculation: build.mutation<
      Offer,
      UpdateTreatmentUseCalculationRequest
    >({
      query: (request) => ({
        url: `/rlt/offer/treatment-price-use-calculation`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltOffer", id: request.id },
        "RltOffer",
        "RltPricing",
      ],
    }),
    rltOfferUpdateIncidentalCostUseCalculation: build.mutation<
      Offer,
      UpdateIncidentalCostUseCalculationRequest
    >({
      query: (request) => ({
        url: `/rlt/offer/incidental-cost-use-calculation`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltOffer", id: request.id },
        "RltOffer",
        "RltPricing",
      ],
    }),
    rltOfferUpdatePostalAddress: build.mutation<
      Offer,
      UpdatePostalAddressRequest
    >({
      query: (request) => ({
        url: `/rlt/offer/postal-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltOffer", id: request.id },
        "RltOffer",
      ],
    }),
    rltOfferUpdateServiceAddress: build.mutation<
      Offer,
      UpdateServiceAddressRequest
    >({
      query: (request) => ({
        url: `/rlt/offer/service-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltOffer", id: request.id },
        "RltOffer",
      ],
    }),
    rltOfferUpdateRepresentative: build.mutation<
      Offer,
      UpdateRepresentativeRequest
    >({
      query: (request) => ({
        url: `/rlt/offer/representative`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltOffer", id: request.id },
        "RltOffer",
      ],
    }),
    rltOfferPdfPreview: build.query<{ objectURL: string }, PDFRequest>({
      ...revokeObjectURLOptions(),
      query: (request) => ({
        url: `/rlt/offer`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: objectURLResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
    }),
    rltOfferPdfDownload: build.mutation<
      { filename: string; objectURL: string },
      PDFRequest
    >({
      query: (request) => ({
        url: `/rlt/offer`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
    }),
    rltOfferUpdateState: build.mutation<Offer, UpdateStateRequest>({
      query: (request) => ({
        url: `/rlt/offer/state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltOffer", id: request.id },
        "RltOffer",
      ],
    }),
    rltOfferDelete: build.mutation<Offer, DeleteRequest>({
      query: (request) => ({
        url: `/rlt/offer`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltOffer", id: request.id },
        "RltOffer",
        "RltPricingByArea",
      ],
    }),
  }),
});

export const {
  useRltOfferCreateMutation,
  useRltOfferCreateFromOldMutation,
  useRltOfferCreateFromCalculationMutation,
  useRltOfferShowQuery,
  useRltOfferListQuery,
  useRltOfferUpdateDateMutation,
  useRltOfferPatchMutation,
  useRltOfferResetReferenceNumberMutation,
  useRltOfferUpdateTreatmentPriceUseCalculationMutation,
  useRltOfferUpdateIncidentalCostUseCalculationMutation,
  useRltOfferUpdatePostalAddressMutation,
  useRltOfferUpdateServiceAddressMutation,
  useRltOfferUpdateRepresentativeMutation,
  useRltOfferUpdateStateMutation,
  useRltOfferPdfDownloadMutation,
  useRltOfferPdfPreviewQuery,
  useLazyRltOfferPdfPreviewQuery,
  useRltOfferDeleteMutation,
} = offerApi;

// PatchRequest updates the document. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged.
export interface PatchRequest {
  id: string;
  referenceNumber?: string;
}

export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateFromOldRequest {
  oldOfferId: string;
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateFromCalculationRequest {
  calculationId: string;
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  orderBy?: OrdrBy<string>[];
  offerDate?: Filter<string>;
  state?: Filter<DocumentState>;
  limit?: number;
  offset?: number;
}

export interface DeleteRequest {
  id: string;
}

export interface UpdateDateRequest {
  id: string;
  date: string;
}

export interface UpdateTreatmentUseCalculationRequest {
  id: string;
  value: boolean;
}

export interface UpdateIncidentalCostUseCalculationRequest {
  id: string;
  value: boolean;
}

export interface UpdatePostalAddressRequest {
  id: string;
  postalAddress: Address;
}

export interface UpdateServiceAddressRequest {
  id: string;
  serviceAddress: Address;
}

export interface UpdateRepresentativeRequest {
  id: string;
  representative: Representative;
}

export enum OfferDocument {
  ALL = "all",
  OFFER = "offer",
  ORDER = "order",
  TREATMENT = "treatment",
  TREATMENT_BY_AREA = "treatment-by-area",
}

export interface PDFRequest {
  id: string;
  document: OfferDocument;
}

export interface UpdateStateRequest {
  id: string;
  state: DocumentState;
  stateSetBy: string;
}

export const updateRltOfferCache = updateCache(offerApi, "rltOfferShow");
